import { IEventLoggingComponentProps, withEventLogging } from '@AppModels/Logging';
import { css, StyleSheet } from 'aphrodite';
import { inject } from 'mobx-react';
import React from 'react';
import { Button } from '../../../../../../../components-v2/Button';
import { THEME } from '../../../../../../../components-v2/Theme/theme';
import { asModalComponent } from '../../../../../../../components/Modal';
import { IModalContext, ModalChildComponentContextKey } from '../../../../../../../../models';
import { SurveySection } from '../../types';
import { ReorderableList } from '../ReorderableList/ReorderableList';

// ---------------------------------------------------------------------------
// Result type
// ---------------------------------------------------------------------------

export interface ReorderSectionsResult {
	reorderedSectionIds?: string[];
	canceled?: boolean;
}

// ---------------------------------------------------------------------------
// Local styles
// ---------------------------------------------------------------------------

const {
	colors,
	typography: { fontSizes },
	spacing,
} = THEME;

const styleSheet = StyleSheet.create({
	emptyState: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		padding: spacing.xl,
		gap: spacing.md,
	},
	emptyTitle: {
		color: colors.text.main,
		fontSize: fontSizes.lg,
		fontWeight: 600,
		textAlign: 'center',
	},
	rowTitle: {
		color: colors.text.main,
		fontSize: fontSizes.sm,
		fontWeight: 500,
	},
	rowCount: {
		color: colors.text.light,
		fontSize: fontSizes.sm,
	},
	modal: {},
});

// ---------------------------------------------------------------------------
// ContentProps — the testable presentational unit
// ---------------------------------------------------------------------------

interface ContentProps {
	sections: SurveySection[];
	onClose: (result?: ReorderSectionsResult, cancel?: boolean) => void;
}

/**
 * Presentational content for the Reorder Sections modal.
 * Contains no MobX — fully testable in isolation.
 */
export const ReorderSectionsModalContent: React.FC<ContentProps> = ({ sections, onClose }) => {
	/** Renders a single row's content for ReorderableList */
	const renderRow = (section: SurveySection) => {
		const title = section.title?.trim() || 'Untitled section';
		const count = section.questions?.length ?? 0;
		const countLabel = `${count} question${count === 1 ? '' : 's'}`;
		return (
			<>
				<div className={css(styleSheet.rowTitle)}>{title}</div>
				<div className={css(styleSheet.rowCount)}>{countLabel}</div>
			</>
		);
	};

	if (sections.length === 0) {
		return (
			<div className={css(styleSheet.emptyState)}>
				<div className={css(styleSheet.emptyTitle)}>No sections to reorder</div>
				<Button variant='secondary' type='button' onClick={() => onClose(undefined, true)}>
					Cancel
				</Button>
			</div>
		);
	}

	return (
		<ReorderableList
			title='Reorder Sections'
			subtitle='Drag and drop sections to reorder them.'
			items={sections}
			droppableId='reorder-sections'
			renderContent={renderRow}
			onCommit={ids => onClose({ reorderedSectionIds: ids }, false)}
			onCancel={() => onClose(undefined, true)}
		/>
	);
};

// ---------------------------------------------------------------------------
// Modal wrapper — mirrors ReorderQuestionsModal structure
// ---------------------------------------------------------------------------

interface IProps extends IEventLoggingComponentProps, IModalContext<ReorderSectionsResult> {
	sections: SurveySection[];
}

const _ReorderSectionsModal: React.FC<IProps> = ({ sections, parentModal }) => {
	return (
		<ReorderSectionsModalContent
			sections={sections}
			onClose={(result, cancel) => {
				if (parentModal) {
					parentModal.onRequestClose(result, cancel);
				}
			}}
		/>
	);
};

const ReorderSectionsModalWithContext = inject(ModalChildComponentContextKey)(_ReorderSectionsModal);
const ReorderSectionsModal = withEventLogging(ReorderSectionsModalWithContext, 'ReorderSectionsModal');

export const ReorderSectionsModalComponent = asModalComponent(ReorderSectionsModal, {
	className: css(styleSheet.modal),
	shouldCloseOnOverlayClick: false,
	useDefaultHeader: true,
});
